import { ArrowClockwiseIcon, CopyIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useApiMutation } from '@/modules/common';

interface SecretKeysProps {
  eventId: string;
  isEditMode: boolean;
}

const formData = [
  {
    label: 'iOS key',
    formKey: 'iosSecretKey',
  },
  {
    label: 'Desktop key',
    formKey: 'desktopSecretKey',
  },
];

export function SecretKeys({ eventId, isEditMode }: SecretKeysProps) {
  const { control } = useFormContext();
  const queryClient = useQueryClient();

  const refreshCredentialsMutation = useApiMutation<void>(
    `/events/${eventId}/credentials/refresh`,
    {
      method: 'POST',
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['eventDetails', String(eventId)],
        });
        toast.success('Credentials refreshed');
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const copyValue = (value: string) => {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        toast('Copied to clipboard');
      });
    }
  };

  return (
    <div className="gap-4 flex flex-col">
      <h3 className="text-2xl font-semibold text-zinc-50">Secret keys</h3>
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
        {formData.map((formField) => (
          <FormField
            control={control}
            name={formField.formKey}
            key={formField.formKey}
            render={({ field }) => (
              <FormItem className={cn(!field.value && 'cursor-not-allowed')}>
                <FormLabel className={cn(!field.value && 'cursor-not-allowed')}>
                  {formField.label}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      readOnly
                      className={cn(
                        'pr-10 focus-visible:ring-0',
                        !field.value && 'cursor-not-allowed'
                      )}
                      placeholder="generates automatically"
                      {...field}
                    />
                    <div
                      data-testid="copy-button"
                      className={cn(
                        'absolute right-2 top-[7px] cursor-not-allowed',
                        field.value && 'cursor-pointer'
                      )}
                      onClick={() => copyValue(field.value)}
                    >
                      <CopyIcon
                        size={30}
                        color={
                          field.value ? colors.zinc[200] : colors.zinc[800]
                        }
                      />
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        ))}
        {eventId && isEditMode && (
          <Button
            variant="outline"
            className="max-w-[150px] self-end h-11"
            isLoading={refreshCredentialsMutation.isPending}
            onClick={() => refreshCredentialsMutation.mutate()}
            startIcon={<ArrowClockwiseIcon size={20} />}
          >
            Refresh keys
          </Button>
        )}
      </div>
    </div>
  );
}
